import { comma } from './utils';

export interface SumColumnConfig {
  /** 합계를 표시할 컬럼의 value (예: 'totalInvestmentAmount') */
  columnValue: string;
  /** 합계 계산에 사용할 sorting 필드명 (예: 'totalInvestmentAmountSorting') */
  sortingField: string;
  /** 합계 값 포맷팅 함수 (예: comma 함수) */
  formatter?: (value: number) => string;
}

export interface TotalRowConfig {
  /** 합계 행의 id (기본값: 'total-row') */
  id?: string;
  /** 합계 행의 라벨을 표시할 컬럼의 value (예: 'name') */
  value?: string;
  /** 합계 행의 라벨 텍스트 (기본값: '합계') */
  label?: string;
}

/**
 * 테이블 데이터에서 지정된 컬럼들의 합계를 계산하고 합계 행을 생성합니다.
 *
 * @param data - 테이블 데이터 배열
 * @param sumColumns - 합계를 계산할 컬럼 설정 배열
 * @param totalRowConfig - 합계 행 설정 (선택사항)
 * @returns 합계 행 객체
 *
 * @example
 * const totalRow = createTotalRow(data, [
 *   { columnValue: 'totalInvestmentAmount', sortingField: 'totalInvestmentAmountSorting', formatter: comma },
 *   { columnValue: 'corporateValue', sortingField: 'corporateValueSorting', formatter: comma },
 * ], { value: 'name', label: '합계' });
 */
export const createTotalRow = (
  data: any[],
  sumColumns: SumColumnConfig[],
  totalRowConfig?: TotalRowConfig
): any => {
  const { id = 'total-row', value, label = '합계' } = totalRowConfig || {};

  // 합계 계산
  const sums: Record<string, number> = {};
  sumColumns.forEach(({ sortingField }) => {
    sums[sortingField] = data.reduce(
      (sum, row) => sum + (row[sortingField] || 0),
      0
    );
  });

  // 합계 행 생성
  const totalRow: any = {
    id,
    isTotalRow: true,
  };

  // 합계 컬럼 값 설정
  sumColumns.forEach(({ columnValue, sortingField, formatter }) => {
    const sumValue = sums[sortingField];
    totalRow[columnValue] = formatter ? formatter(sumValue) : sumValue;
    totalRow[sortingField] = sumValue;
  });

  // 라벨 컬럼 설정
  if (value) {
    totalRow[value] = label;
  }

  return totalRow;
};

/**
 * 테이블 데이터에 합계 행을 추가합니다.
 *
 * @param data - 테이블 데이터 배열
 * @param sumColumns - 합계를 계산할 컬럼 설정 배열
 * @param totalRowConfig - 합계 행 설정 (선택사항)
 * @returns 합계 행이 추가된 데이터 배열
 *
 * @example
 * const dataWithTotal = addTotalRow(data, [
 *   { columnValue: 'totalInvestmentAmount', sortingField: 'totalInvestmentAmountSorting', formatter: comma },
 * ]);
 */
export const addTotalRow = (
  data: any[],
  sumColumns: SumColumnConfig[],
  totalRowConfig?: TotalRowConfig
): any[] => {
  if (!data || data.length === 0) {
    return data;
  }

  const totalRow = createTotalRow(data, sumColumns, totalRowConfig);
  return [...data, totalRow];
};

/**
 * 테이블 헤더에서 합계 컬럼 설정을 추출합니다.
 *
 * @param header - 테이블 헤더 배열
 * @param defaultFormatter - 기본 포맷터 함수 (기본값: comma)
 * @returns 합계 컬럼 설정 배열
 *
 * @example
 * const sumColumns = extractSumColumnsFromHeader(PORTFOLIO_LIST_HEADER);
 */
export const extractSumColumnsFromHeader = (
  header: any[],
  defaultFormatter: (value: number) => string = comma
): SumColumnConfig[] => {
  return header
    .filter((h) => h.isSumColumn === true)
    .map((h) => ({
      columnValue: h.value,
      sortingField: h.sortingField || `${h.value}Sorting`,
      formatter: h.sumFormatter || defaultFormatter,
    }));
};
